import { useQuery } from '@tanstack/react-query';
import useAuth from '../../auth/feature/auth';
import { managementService } from '../../management/service/management.service';

export const STANDBY_PRODUCT_SLUG = 'standby';

/**
 * Slug of the Squarebit Eye entitlement. Must match what the admin panel
 * writes into product_access_grants and what the API checks for.
 *
 * Named rather than spelled out at each call site because the Eye is gated
 * in several places at once - its card on My Products, its pricing - and a
 * typo in any one of them fails OPEN, showing what it was meant to hide.
 */
export const EYE_PRODUCT_SLUG = 'squarebit-eye';

/**
 * Admin-granted product entitlements for the logged-in account.
 *
 * Shares the ['my-access'] key with My Products, Settings and the product
 * pages, so however many places ask, it is fetched once.
 *
 * This is a convenience for deciding what to *render*. It is not the security
 * boundary — anything that must not leak is served from behind a grant check
 * on the API (see the Standby endpoints), because the frontend bundle reaches
 * every visitor regardless of what it chooses to draw.
 */
export const useProductAccess = () => {
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: ['my-access'],
    queryFn: async () => managementService.getMyAccess(),
    enabled: !!isAuthenticated
  });

  const products = (isAuthenticated && query.data) || [];

  return {
    products,
    // `isAuthenticated` is null until the session check comes back, and the
    // query is disabled until then, so a plain `isAuthenticated && isLoading`
    // reports "done, no grants" during that window. Anything that only decides
    // what to draw recovers on the next render; a route guard does not, so the
    // unresolved case has to count as loading.
    isLoading: isAuthenticated === null || (!!isAuthenticated && query.isLoading),
    has: (slug: string) => products.includes(slug)
  };
};

/** Convenience for the common single-slug case. */
export const useHasProductAccess = (slug: string): boolean =>
  useProductAccess().has(slug);
